import { XBOT_MESSAGE_VERSION, XbotBlockTone as XbotComposedTone, XbotClaim, XbotMessage, XbotMessageBlock } from "@dto/pyxbot-agent.dto";
import { upperFirst } from "lodash-es";
import { FC, ReactNode } from "react";
import { LuCircleHelp, LuCirclePause, LuCircleX, LuSearchX, LuTriangleAlert } from "react-icons/lu";
import { codeView, inlineNodes, listView, markdownPieces, namedNodes, XbotPageNames } from "./markdownText";

// Tone comes from the composer's claims/state at compose time, never from parsing words.
// "error" means an actual failure; a refusal, empty result or question is never drawn red.
export type XbotTone = "normal" | "cannot-answer" | "nothing-found" | "question" | "error";

export interface XbotToneSegment {
  markdown: string;
  tone: XbotTone;
}

/** What XbotAnswer hands a block about its look. */
export interface XbotBlockTone {
  tone: XbotTone;
  // Only for a text block with mixed tones: markdown cut into joined stretches, or null if the cut can't be proven.
  segments: XbotToneSegment[] | null;
}

export const NORMAL_TONE: XbotBlockTone = { tone: "normal", segments: null };

// A Map rather than an object, so a kind like "constructor" from a newer service finds nothing.
const KIND_TONE: ReadonlyMap<string, XbotTone> = new Map<string, XbotTone>([
  ["unavailable", "cannot-answer"],
  ["absence", "nothing-found"],
  ["none_passed", "nothing-found"],
  ["question", "question"],
]);

// The kinds of block that show data. They keep their own look; only sentence/text and question blocks get a tone card.
export const DATA_BLOCK_TYPES: readonly string[] = ["value", "table", "comparison", "timeseries", "distribution", "relations"];

// The composer's words for a tone, as the page names the same looks.
const COMPOSED_TONE: ReadonlyMap<string, XbotTone> = new Map<string, XbotTone>([
  ["cannot_answer", "cannot-answer"],
  ["nothing_found", "nothing-found"],
  ["question", "question"],
]);

// undefined = older service wrote no tone (falls back to claims); an unknown word reads as "normal".
export function composedTone(block: XbotMessageBlock | null | undefined): XbotTone | undefined {
  if (!block || typeof block !== "object" || !("tone" in block) || block.tone === undefined) {
    return undefined;
  }
  const tone: XbotComposedTone | null = block.tone;
  return (typeof tone === "string" && COMPOSED_TONE.get(tone)) || "normal";
}

export function toneOfKind(kind: string | null | undefined): XbotTone {
  return (typeof kind === "string" && KIND_TONE.get(kind)) || "normal";
}

export function toneOfClaim(claim: XbotClaim | null | undefined): XbotTone {
  if (!claim || typeof claim !== "object") {
    return "normal";
  }
  const tone = toneOfKind(claim.kind);
  if (tone !== "normal") {
    return tone;
  }
  // A count of nothing is said with the same sentence as an absence: NG returned nothing.
  return claim.kind === "count" && claim.count === 0 ? "nothing-found" : "normal";
}

// null for an unknown schema version: its claims/block types might mean something else, so nothing is set apart.
export function claimsOf(message: XbotMessage | null | undefined): ReadonlyMap<string, XbotClaim> | null {
  if (message?.schema_version !== XBOT_MESSAGE_VERSION) {
    return null;
  }
  const byId = new Map<string, XbotClaim>();
  for (const claim of Array.isArray(message.claims) ? message.claims : []) {
    if (claim && typeof claim === "object" && typeof claim.id === "string") {
      byId.set(claim.id, claim);
    }
  }
  return byId;
}

function oneTone(tones: XbotTone[]): XbotTone | null {
  return tones.length && tones.every((tone) => tone === tones[0]) ? tones[0] : null;
}

// Rebuilds the composer's per-claim markdown and diffs it against the block's actual text.
// Only an exact match proves which stretch belongs to which claim; anything else falls back to no split.
export function textSegments(markdown: string, claims: XbotClaim[]): XbotToneSegment[] | null {
  const order: string[] = [];
  const tones = new Map<string, XbotTone[]>();
  for (const claim of claims) {
    const text = claim && typeof claim.text === "string" ? claim.text : null;
    if (text === null) {
      return null;
    }
    const held = tones.get(text);
    if (held) {
      held.push(toneOfClaim(claim));
    } else {
      order.push(text);
      tones.set(text, [toneOfClaim(claim)]);
    }
  }
  const segments = order.map((text): XbotToneSegment => {
    const said = tones.get(text) ?? [];
    const markdownOf = said.length > 1 ? `${text.endsWith(".") ? text.slice(0, -1) : text} (${said.length} times).` : text;
    return { markdown: markdownOf, tone: oneTone(said) ?? "normal" };
  });
  if (!segments.length || segments.map((segment) => segment.markdown).join("\n\n") !== markdown) {
    return null;
  }
  const joined: XbotToneSegment[] = [];
  for (const segment of segments) {
    const last = joined[joined.length - 1];
    if (last?.tone === segment.tone) {
      last.markdown = `${last.markdown}\n\n${segment.markdown}`;
    } else {
      joined.push({ ...segment });
    }
  }
  return joined;
}

// Composer's tone wins when present; older services fall back to claims (question blocks are always a question card).
export function toneOfBlock(
  block: XbotMessageBlock | null | undefined,
  claims: ReadonlyMap<string, XbotClaim> | null,
): XbotBlockTone {
  if (!claims || !block || typeof block !== "object" || block.state !== "ready") {
    return NORMAL_TONE;
  }
  const type = String(block.type);
  const composed = composedTone(block);
  if (composed !== undefined) {
    // What draws data is the answer itself and keeps its look, whatever tone came with it.
    return composed === "normal" || DATA_BLOCK_TYPES.includes(type) ? NORMAL_TONE : { tone: composed, segments: null };
  }
  if (type === "question") {
    return { tone: "question", segments: null };
  }
  if (DATA_BLOCK_TYPES.includes(type)) {
    return NORMAL_TONE;
  }
  const ids = Array.isArray(block.claim_ids) ? block.claim_ids : [];
  const own = ids.map((id) => claims.get(id)).filter((claim): claim is XbotClaim => Boolean(claim));
  // Every claim must be known: a missing one could let the rest give a false, one-sided tone.
  if (!own.length || own.length !== ids.length) {
    return NORMAL_TONE;
  }
  const whole = oneTone(own.map(toneOfClaim));
  if (whole !== null) {
    return whole === "normal" ? NORMAL_TONE : { tone: whole, segments: null };
  }
  const config = block.config as { markdown?: unknown } | null | undefined;
  const markdown = type === "text" ? config?.markdown : null;
  const segments = typeof markdown === "string" ? textSegments(markdown, own) : null;
  return segments?.some((segment) => segment.tone !== "normal") ? { tone: "normal", segments } : NORMAL_TONE;
}

// The card a block got above, as it is drawn.

/**
 * The head of a card, on a line of its own above what it says: one shape for every answer that is not one,
 * its colour by how much it matters.
 */
export const TONE_HEAD: Readonly<Record<XbotTone, string>> = {
  "normal": "",
  "cannot-answer": "Couldn't answer",
  "nothing-found": "Nothing found",
  "question": "",
  "error": "The answer failed",
};

/** The opening of a refusal that only says it is one: the card's head says the same, so it is left out. */
const GENERIC_OPENING = "I could not answer this question: ";

/**
 * What a card says under its head: a refusal's generic "I could not answer this question: <reason>" says only the
 * reason ("the data is not there" -> "The data is not there"), the head says the rest. An opening that names what was not answered ("I could not answer this for app02:
 * …") is kept whole, so the name stays in the sentence.
 */
export function toneBody(tone: XbotTone, text: string): string {
  return tone === "cannot-answer" && text.startsWith(GENERIC_OPENING) && text.length > GENERIC_OPENING.length
    ? upperFirst(text.slice(GENERIC_OPENING.length))
    : text;
}

export function calloutClass(tone: XbotTone, stopped = false): string {
  return stopped ? `xm-xbot-callout xm-xbot-callout-${tone} xm-xbot-callout-stopped` : `xm-xbot-callout xm-xbot-callout-${tone}`;
}

// What happened at a glance - a question, nothing found, a refusal, a stop, a failure.
export const CalloutIcon: FC<{ tone: XbotTone; stopped?: boolean }> = ({ tone, stopped }) => {
  const icon = stopped ? <LuCirclePause />
    : tone === "question" ? <LuCircleHelp />
      : tone === "error" ? <LuCircleX />
        : tone === "cannot-answer" ? <LuTriangleAlert />
          : tone === "nothing-found" ? <LuSearchX />
            : null;
  return icon ? <span className="xm-xbot-callout-icon" aria-hidden="true">{icon}</span> : null;
};

export const XbotCallout: FC<{ tone: XbotTone; stopped?: boolean; children: ReactNode }> = ({ tone, stopped: stoppedProp, children }) => {
  // defaulted in the body: the React Compiler skips a component with a default in its destructured props
  const stopped = stoppedProp ?? false;
  return <div className={calloutClass(tone, stopped)} data-tone={tone} role="note">
    <CalloutIcon tone={tone} stopped={stopped} />
    <div className="xm-xbot-callout-body">{children}</div>
  </div>;
};

/** A card's head line. */
export const CalloutHead: FC<{ text: string }> = ({ text }) => <div className="xm-xbot-callout-head">{text}</div>;

// Same rendering as any text block's markdown (its names linked alike), with the card's head on a line of its own
// above the sentences.
export const TonedMarkdown: FC<{ markdown: string; tone: XbotTone; names?: XbotPageNames | null }> = ({ markdown, tone, names }) => (
  <div className="xm-xbot-markdown">
    {TONE_HEAD[tone] ? <CalloutHead text={TONE_HEAD[tone]} /> : null}
    {markdownPieces(markdown).map((piece, index) => (piece.kind === "p"
      ? <p key={index}>{inlineNodes(index === 0 ? toneBody(tone, piece.text) : piece.text, names)}</p>
      : piece.kind === "pre" ? codeView(piece, index) : listView(piece, index, names)))}
  </div>
);

export const TonedPlainText: FC<{ text: string; tone: XbotTone; names?: XbotPageNames | null }> = ({ text, tone, names }) => (TONE_HEAD[tone]
  ? <><CalloutHead text={TONE_HEAD[tone]} />{namedNodes(toneBody(tone, text), names)}</>
  : <>{namedNodes(text, names)}</>);
